import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, ChevronLeft, ShieldCheck, XCircle } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { EmptyState, PermissionDenied, Skeleton } from '@/components/States'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import {
  CAMP_STR as S,
  STATUS_KIND,
  broadcastAction,
  fetchBroadcastsRaw,
  type BroadcastLite,
} from './campaignsApi'

const AUD_LABEL: Record<string, string> = {
  all: 'Все пользователи',
  active: 'Активные',
  staff: 'Персонал',
}

/**
 * A15.08 — Tasdiqlash oqimi (four-eyes). Draft/queued kampaniyalar ikkinchi
 * tasdiqlovchini kutadi. «Согласовать» → draft'ni navbatga (queue), «Отклонить»
 * → cancel. Ikkalasi ham REAL: POST /admin/broadcasts/:id/action. Ma'lumot manbai
 * REAL GET /admin/broadcasts (klient tomonда draft/queued bo'yicha filtrlanadi).
 */
export function ApprovalFlow() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [rows, setRows] = useState<BroadcastLite[] | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    fetchBroadcastsRaw(ac.signal).then((all) =>
      setRows(all.filter((r) => r.status === 'draft' || r.status === 'queued')),
    )
    return () => ac.abort()
  }, [tick])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const act = async (id: number, action: 'queue' | 'cancel') => {
    setBusyId(id)
    try {
      await broadcastAction(id, action)
      toast(action === 'queue' ? S.approved : S.rejected, { tone: 'success' })
      reload()
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <PageHeader
        title={S.approvalTitle}
        screenId="A15.08"
        description={S.approvalNote}
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-3xl space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-2 px-3 py-2 text-xs text-tertiary">
            <ShieldCheck size={14} className="text-accent" />
            {S.fourEyesHint}
          </div>
          <div className="text-xs font-medium uppercase tracking-wide text-tertiary">{S.awaiting}</div>
          {rows === null ? (
            <div className="space-y-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<ShieldCheck size={26} strokeWidth={1.5} />} title={S.noPending} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={(STATUS_KIND[r.status] as StatusKind) ?? 'pending'} />
                        <span className="text-sm font-medium text-primary">{r.title || `#${r.id}`}</span>
                        <span className="text-xs text-tertiary">{AUD_LABEL[r.audience] ?? r.audience}</span>
                      </div>
                      <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-secondary">{r.body}</p>
                      <p className="mt-0.5 text-xs text-tertiary">{new Date(r.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-1.5">
                      <button
                        disabled={busyId === r.id || r.status !== 'draft'}
                        onClick={() => act(r.id, 'queue')}
                        className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-40"
                      >
                        <CheckCircle2 size={13} />
                        {S.approve}
                      </button>
                      <button
                        disabled={busyId === r.id}
                        onClick={() => act(r.id, 'cancel')}
                        className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-3 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-40"
                      >
                        <XCircle size={13} />
                        {S.reject}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
